/*
Utilities required by other RouteUtils.
*/
import { Map } from 'immutable';

import { type State, type ValueOf } from 'src/types';

import { UrlSegments } from '../../constants/App';
import { SourceTypeUrlParams } from '../../constants/Catalog';
import { SourceTypes } from '../../constants/SourceTypes';
import { Urls } from '../../constants/Urls';
import { getInDomain } from '../../domains/externalUtils';
import Logger from '../../tools/Logger';
import { isWindowsClient } from '../ClientInfoUtils';
import { getJustMigratedWindowsAccount, isAPSPandoraStationEnabled } from '../ListenerUtils';
import { isOnDemandIdLike } from '../MusicUtils';
import { getSourceTypeFromPandoraId } from '../SourceTypeUtils';

const log = Logger.child({ component: 'lib/RouteUtils' });

export const ARTIST_BASE_PATH = '/artist';
export const BROWSE_BASE_PATH = '/browse';
export const COLLECTION_BASE_PATH = '/collection';
export const CURATOR_BASE_PATH = '/curator';
export const MY_MUSIC_BASE_PATH = '/my-music';
export const PODCAST_BASE_PATH = '/podcast';
export const SEARCH_BASE_PATH = '/search';
export const STATION_BASE_PATH = '/station';

// TODO: WEBCLIENT-6245 Remove this when search unification launches 100%
export const searchRegex = /^\/search$/;

export function getMyMusicPath() {
  return `${MY_MUSIC_BASE_PATH}` as const;
}

export function getMyMusicAllFilterPath() {
  return getMyMusicPath();
}

/**
 * Get the current path from the state. May not match the actual URL eg. in the case
 * of search where we don't want to update the router history until the search is complete
 *
 * @param {object} state - app state
 * @returns {string} current router path (NOT ALWAYS ACTUAL URL)
 */
export function getCurrentPathnameFromState(state: State): string {
  return state?.routing?.locationBeforeTransitions?.pathname;
}

export function getSettingsPath() {
  return '/settings' as const;
}

export function getSubscriptionPath() {
  return `${getSettingsPath()}/subscription` as `${ReturnType<
    typeof getSettingsPath
  >}/subscription`;
}

export function getHelpUrl() {
  return `${Urls.HELP}` as const;
}

export function createHelpUrl<Route extends string>(route: Route) {
  return `${getHelpUrl()}${route}` as `${ReturnType<typeof getHelpUrl>}${Route}`;
}

export function isStationBasePath(path: string): boolean {
  return /^\/station(?:\/(.*))?$/g.test(path);
}

export function isStationDetailsPath(path: string): boolean {
  return /^\/station\/\w+$/i.test(path);
}

export function isStationThumbsUpPath(path: string): boolean {
  return /^\/station\/thumbed-up\/\w+$/i.test(path);
}

export function getStationIdFromStationDetailsPath(path: string): string | null {
  const result = /^\/station\/(\w+)/i.exec(path);
  return result ? result[1] : null;
}

export function isOnDemandNowPlayingRoute(path = ''): boolean {
  const ON_DEMAND_NP_PATH_REGEX = new RegExp(/(playlist|artist|my-music)\/play\//);
  return (
    ON_DEMAND_NP_PATH_REGEX.test(path) ||
    (path.indexOf(STATION_BASE_PATH) === 0 && path.includes('thumbed-up'))
  );
}

export function isNowPlayingPodcastRoute(path: string): boolean {
  const ON_DEMAND_NP_PLAYLIST_PATH_REGEX = new RegExp(/podcast\/play\//);
  return ON_DEMAND_NP_PLAYLIST_PATH_REGEX.test(path);
}

export function isPathInNowPlayingPath(path: string): boolean {
  if (!path) return false;
  const isStation = isStationBasePath(path) && !isStationDetailsPath(path);
  return isStation || isOnDemandNowPlayingRoute(path) || isNowPlayingPodcastRoute(path);
}

/* Route Mutation Utilities - Start */
// TODO: WEBCLIENT-3322 – Investigate implications of using hash routes and impact on analytics
export function stripTrailingSlash(pathname: string): string {
  return pathname.replace(/\/$/, '');
}

export function getAccountPath() {
  return '/account' as const;
}

export function getLoginPath() {
  return (getAccountPath() + '/sign-in') as `${ReturnType<typeof getAccountPath>}/sign-in`;
}

export function getRegisterPath() {
  return (getAccountPath() + '/register') as `${ReturnType<typeof getAccountPath>}/register`;
}

export function getMobileRegisterWelcomePath() {
  return (getRegisterPath() + '/welcome') as `${ReturnType<typeof getRegisterPath>}/welcome`;
}

export function getMobileSignInWelcomePath() {
  return (getLoginPath() + '/welcome') as `${ReturnType<typeof getLoginPath>}/welcome`;
}

export function getNotFoundPath() {
  return 'NotFound' as const;
}

export function isMobileLogInWelcomePath(pathname: string): boolean {
  return /^\/account\/sign-in\/welcome\/?$/.test(pathname);
}

export function isRegisterPath(pathname: string): boolean {
  return /^\/account\/register\/?$/.test(pathname);
}

function getSlugPlusPandoraIdForPseudoSource(state: State, playlistId: string): string {
  const id = getInDomain('catalog.utils.mapPseudoSourceToParent', null)(state, playlistId);
  const annotations = getInDomain('catalog.utils.getAnnotations', Map())(state, id);
  return annotations.get('slugPlusPandoraId', '');
}

function getDefaultNowPlayingPath<BasePath extends string, Id extends string>(
  basePath: BasePath,
  id: Id,
): string {
  return `/${basePath}/${UrlSegments.PLAY}/${id}` as `${BasePath}/${typeof UrlSegments.PLAY}/${Id}`;
}

/**
 * Route to playback URL for a station code
 *
 * @param {string} stationCode - stationId, musicId or seo token
 * @returns {string} playback URL
 */
export function getStationPlayPathForStationCode<StationCode extends string>(
  stationCode: StationCode,
): string {
  if (!stationCode) {
    return STATION_BASE_PATH;
  }
  return `${STATION_BASE_PATH}/${UrlSegments.PLAY}/${stationCode}` as `${typeof STATION_BASE_PATH}/${typeof UrlSegments.PLAY}/${StationCode}`;
}

export function getAllSongsNowPlayingPath<SlugPlusPandoraId extends string>(
  slugPlusPandoraId: SlugPlusPandoraId,
): string {
  return `/${SourceTypeUrlParams.ARTIST}/${UrlSegments.PLAY}/${SourceTypeUrlParams.ALL_SONGS}${slugPlusPandoraId}` as `/${typeof SourceTypeUrlParams.ARTIST}/${typeof UrlSegments.PLAY}/${typeof SourceTypeUrlParams.ALL_SONGS}${SlugPlusPandoraId}`;
}

export function getNowPlayingPathForId(
  state: State,
  id: string,
  sourceType: ValueOf<typeof SourceTypes>,
): string {
  // $TSFixMe: define more specific string return
  let slugPlusPandoraId: string;
  let annotations: $TSFixMe;
  const thumbPrintStationId = getInDomain('playback.utils.getThumbprintStationId', '')(state);
  const isAPSEnabled = isAPSPandoraStationEnabled(state);
  switch (sourceType) {
    case SourceTypes.ARTIST_TOP_SONGS:
      slugPlusPandoraId = getSlugPlusPandoraIdForPseudoSource(state, id);
      return `/${SourceTypeUrlParams.ARTIST}/${UrlSegments.PLAY}/${SourceTypeUrlParams.TOP_SONGS}${slugPlusPandoraId}`;
    case SourceTypes.ARTIST_ALL_SONGS:
      slugPlusPandoraId = getSlugPlusPandoraIdForPseudoSource(state, id);
      return getAllSongsNowPlayingPath(slugPlusPandoraId);
    case SourceTypes.COLLECTED_TRACKS:
      return `/${SourceTypeUrlParams.MY_MUSIC}/${UrlSegments.PLAY}/${SourceTypeUrlParams.ALL_SONGS}`;
    case SourceTypes.COLLECTED_ALBUM_TRACKS:
      slugPlusPandoraId = getSlugPlusPandoraIdForPseudoSource(state, id);
      return `/${SourceTypeUrlParams.ARTIST}/${UrlSegments.PLAY}${slugPlusPandoraId}`;
    case SourceTypes.STATION_THUMB_UP:
      return `/${SourceTypeUrlParams.STATION}/${UrlSegments.PLAY}/${SourceTypeUrlParams.THUMBED_UP}/${id}`;
    case SourceTypes.SONG:
    case SourceTypes.ALBUM:
      annotations = getInDomain('catalog.utils.getAnnotations', Map())(state, id);
      return `/${SourceTypeUrlParams.ARTIST}/${UrlSegments.PLAY}${annotations.get(
        'slugPlusPandoraId',
        '',
      )}`;
    case SourceTypes.AUTOPLAY: {
      const originalSourceId = getInDomain('playback.selectors.getSourcePandoraId', '')(state);
      const originalSourceType = originalSourceId && getSourceTypeFromPandoraId(originalSourceId);

      // original source should never be AUTOPLAY, but want to prevent the possibility of infinite recursion in case of a bug
      if (originalSourceType === SourceTypes.AUTOPLAY) {
        log.error(`Original AUTOPLAY source type for id: ${id} was AUTOPLAY`);
        return '';
      }
      return getNowPlayingPathForId(state, originalSourceId, originalSourceType);
    }
    case SourceTypes.STATION: {
      const pandoraId = isAPSEnabled && id && id.includes(thumbPrintStationId) ? 'TT:0' : id;
      const stationId = isOnDemandIdLike(id)
        ? getInDomain('playback.utils.getStationIdFromPandoraId', '')(state, pandoraId)
        : id;
      return getStationPlayPathForStationCode(stationId);
    }
    case SourceTypes.PLAYLIST:
      return getDefaultNowPlayingPath(SourceTypeUrlParams.PLAYLIST, id);
    case SourceTypes.PODCAST:
    case SourceTypes.PODCAST_AUTOPLAY:
      annotations = getInDomain('catalog.utils.getAnnotations', Map())(
        state,
        getInDomain('playback.utils.getPlaylistCurrentToken', null)(state, id),
      );
      return `/${SourceTypeUrlParams.PODCAST}/${UrlSegments.PLAY}${annotations.get(
        'slugPlusPandoraId',
        '',
      )}`;
    default:
      log.error(`Failed to match now playing url for type: ${sourceType}`);
      return '';
  }
}

export function getNowPlayingPath(state: State): string {
  if (!state) {
    return STATION_BASE_PATH;
  }

  // For users that have just migrated to Windows, we need to make the now playing link to collection
  if (getJustMigratedWindowsAccount(state) && isWindowsClient()) {
    return COLLECTION_BASE_PATH;
  }

  // @todo - There is no way to ensure that these domains have been loaded yet - WEBCLIENT-3137
  const playlistId = getInDomain('playback.utils.getCurrentPlaylist', null)(state);
  const playlistType = getInDomain('playback.utils.getCurrentPlaylistType', null)(state);

  if (playlistId && playlistType) {
    return getNowPlayingPathForId(state, playlistId, playlistType);
  }

  return STATION_BASE_PATH;
}

export function isLoginPath(pathname: string): boolean {
  return /^\/account\/sign-in\/?$/.test(pathname);
}

export function isLogoutPath(pathname: string): boolean {
  return /^\/account\/sign-out\/?$/.test(pathname);
}

export function getSubscriptionWelcomePath() {
  return '/subscription/welcome' as const;
}

export function isSubscriptionWelcomePath(pathname: string): boolean {
  return /^\/subscription\/welcome\/?$/.test(pathname);
}

/* used by the router to render a blank component when the window location is set to the community sso url */
export function getCommunityRedirectPath() {
  return '/account/community-redirect' as const;
}

export function getParamFromProps<PropKey extends string>(
  props: { params: Record<PropKey, string> },
  paramName: PropKey,
): string {
  return props.params[paramName];
}
